import classNames from 'classnames'
import isEqual from 'lodash/isEqual'
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { ERRORS } from '../../../constants'
import { isLegacyServiceSale } from '../../../functions/sale'
import {
  FulfilmentType,
  isDefinedFulfilmentType,
} from '../../../models/fulfilment'
import { LineItem } from '../../../models/line-item'
import {
  focusGlobalSearch,
  useSaleInventoryData,
  useSaleManager,
  useVdFeatureManager,
} from '../../../ng-compat/hooks'
import { $rootScope } from '../../../ng-compat/imports'
import { useAppDispatch, useAppSelector } from '../../../state/hooks'
import { selectSaleStatus } from '../../../state/slices/currentSale/currentSaleSlice'
import {
  openFulfilmentLineItemPopover,
  removeLineItemInputErrors,
  removeLineItemQuantityInputError,
  selectExpandedLineItemId,
  selectIsOnlyLineItemQuantityInputError,
  selectLineItemQuantityInputError,
  selectSingleLineItemHasErrors,
  setLineItemQuantityInputError,
} from '../../../state/slices/uiStateSlice'
import { useSaleHasServiceOrder } from '../useSaleHasServiceOrder'
import { useSaleIsContinuedServiceSale } from '../useSaleIsContinuedServiceSale'
import { LineItemDisplay } from './LineItemDisplay'
import { LineItemExpanded } from './LineItemExpanded'
import { useLineItemHasFulfillmentAddressError } from './fulfilment/hook/useLineItemHasFulfillmentAddressError'

interface Props {
  lineItem: LineItem
}

/**
 * Scroll the line item into view if it's out of view. This also prevent over-scrolling if the line item is already in view.
 * The issue is because there are a lot of css transitions triggered in the line item component.
 * We only want to trigger the scroll if the transition is complete and if the element if out of sale-body class only.
 */
const scrollIfOutOfView = (ref: React.RefObject<HTMLUListElement>) => {
  const saleBodyNode = document.querySelector('.sale-body')
  if (!ref.current || !saleBodyNode) {
    return
  }

  const outerElementNodeRect = saleBodyNode?.getBoundingClientRect()
  const innerElementNodeRect = ref.current.getBoundingClientRect()
  const isOutOfView =
    innerElementNodeRect.top < outerElementNodeRect.top ||
    innerElementNodeRect.bottom > outerElementNodeRect.bottom
  if (isOutOfView) {
    ref.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }
}

const SaleLineItemComponent: FunctionComponent<Props> = ({ lineItem }) => {
  const dispatch = useAppDispatch()

  const expandedLineItem = useAppSelector(selectExpandedLineItemId)
  const saleStatus = useAppSelector(selectSaleStatus)

  const [elementToFocusOnExpand, setElementToFocusOnExpand] = useState<
    'quantity' | 'price'
  >('quantity')
  const lineItemHasInputErrors = useAppSelector(
    selectSingleLineItemHasErrors(lineItem.localId)
  )
  const hasAddressErrorForLineItemFulfillmentDelivery =
    useLineItemHasFulfillmentAddressError() &&
    lineItem.fulfilmentType === FulfilmentType.DELIVERY

  const onlyLineItemQuantityInputError = useAppSelector(
    selectIsOnlyLineItemQuantityInputError(lineItem.localId)
  )
  const lineItemQtyErr = useAppSelector(
    selectLineItemQuantityInputError(lineItem.localId)
  )

  const saleHasServiceOrder = useSaleHasServiceOrder()
  const isContinuedServiceSale = useSaleIsContinuedServiceSale()

  const onlyNoInvError =
    lineItemHasInputErrors &&
    onlyLineItemQuantityInputError &&
    lineItemQtyErr === ERRORS.NO_INVENTORY

  const hasNonNoInventoryInputErrors = lineItemHasInputErrors && !onlyNoInvError

  // For new service sales, NO_INVENTORY errors should not show warning icon
  // (JAWS handles fulfillment for service orders)
  // For continued service sales, show the warning icon so user can click "Continue anyway"
  const shouldShowInputError =
    saleHasServiceOrder && !isContinuedServiceSale
      ? hasNonNoInventoryInputErrors
      : lineItemHasInputErrors

  // if the line item only has a noInventory error and we selected 'continue anyway', we can ignore it
  const onSelectContinueAnyway = () => {
    if (onlyNoInvError) {
      if (lineItem) {
        dispatch(removeLineItemQuantityInputError(lineItem.localId))
      }
    }
  }

  const saleInventoryData = useSaleInventoryData()

  // Keep a ref to current lineItem for use in callbacks that need fresh data
  const lineItemRef = useRef(lineItem)
  lineItemRef.current = lineItem

  const onSelectFulfilmentType = (type: FulfilmentType) => {
    // this will not remove the error if the fulfilment type is not defined
    if (onlyNoInvError && isDefinedFulfilmentType(type)) {
      dispatch(removeLineItemQuantityInputError(lineItem.localId))
    } else if (
      !isDefinedFulfilmentType(type) &&
      !saleInventoryData.hasInventory(lineItem)
    ) {
      dispatch(
        setLineItemQuantityInputError({
          id: lineItem.localId,
          error: ERRORS.NO_INVENTORY,
        })
      )
    }
  }

  const editAllowedForConfirmed = useCallback(() => {
    return isLegacyServiceSale(saleStatus)
  }, [saleStatus])

  const saleManager = useSaleManager()
  const isFulfillmentLifB3FFEnabled = useVdFeatureManager().get(
    'fulfillment_lif_b3'
  )

  // For service sales with OOS items, open the decision popover
  // The user will choose: "Add product anyway. It's available." or "Request to order this product"
  useEffect(() => {
    if (!saleHasServiceOrder || isContinuedServiceSale) {
      return
    }

    if (isDefinedFulfilmentType(lineItem.fulfilmentType)) {
      return
    }

    if (!lineItem.product.has_inventory) {
      return
    }

    if (!isFulfillmentLifB3FFEnabled) {
      return
    }

    const maybeOpenDecisionPopover = () => {
      const currentLineItem = lineItemRef.current

      // Don't open if validation was disabled (user already clicked "Continue anyway")
      if (!saleInventoryData.shouldValidateInventory(currentLineItem)) {
        return
      }

      if (saleInventoryData.hasInventory(currentLineItem)) {
        return
      }

      // Open the decision popover for OOS items
      dispatch(openFulfilmentLineItemPopover(currentLineItem.localId))
    }

    maybeOpenDecisionPopover()
  }, [
    dispatch,
    isContinuedServiceSale,
    isFulfillmentLifB3FFEnabled,
    lineItem.localId,
    lineItem.fulfilmentType,
    lineItem.product.has_inventory,
    saleHasServiceOrder,
    saleInventoryData,
  ])

  const [isTransitionComplete, setTransitionComplete] = useState(false)
  const [justRendered, setJustRendered] = useState(true) // Applied when the line item is already expanded but it's rendered in different parts of line item. The transition in css won't be applied but we should still scroll into view

  const removeLineItem = () => {
    saleManager.removeLineItem(lineItem).then(() => {
      dispatch(removeLineItemInputErrors(lineItem.localId))

      focusGlobalSearch()
      $rootScope.$emit('sale:remove-line-item', lineItem)
    })
  }

  const ref = useRef<HTMLUListElement>(null)

  useEffect(() => {
    if (
      (isTransitionComplete || justRendered) &&
      ref.current &&
      expandedLineItem === lineItem.localId
    ) {
      setJustRendered(false)
      scrollIfOutOfView(ref)
    }
  }, [expandedLineItem, isTransitionComplete, justRendered, lineItem.localId])

  const handleTransitionEnd = () => {
    setTransitionComplete(true)
  }

  useEffect(() => {
    const handleTransitionStart = (e: TransitionEvent) => {
      if (e.target === ref.current) {
        setTransitionComplete(false)
      }
    }

    const element = ref.current
    if (element) {
      element.addEventListener('transitionstart', handleTransitionStart)
    }

    return () => {
      if (element) {
        element.removeEventListener('transitionstart', handleTransitionStart)
      }
    }
  }, [])

  return (
    <ul
      className={classNames('li-cell', {
        'li-cell--expanded': expandedLineItem === lineItem.localId,
      })}
      data-line-item-id={lineItem.localId}
      data-adjusted={lineItem.adjusted}
      ref={ref}
      data-testid={`line-item-${lineItem.localId}`}
      onTransitionEnd={handleTransitionEnd}
    >
      <li>
        <LineItemDisplay
          hasInvalidInput={
            shouldShowInputError ||
            hasAddressErrorForLineItemFulfillmentDelivery
          }
          lineItem={lineItem}
          editAllowedForConfirmed={editAllowedForConfirmed()}
          onRemove={removeLineItem}
          onToggle={(event: React.MouseEvent) => {
            if (
              event.target instanceof HTMLElement &&
              event.target.classList.contains('wr-li-cell-summary-price-value')
            ) {
              setElementToFocusOnExpand('price')
            } else {
              setElementToFocusOnExpand('quantity')
            }
          }}
          onSelectContinueAnyway={onSelectContinueAnyway}
          onSelectFulfilmentType={onSelectFulfilmentType}
        />
      </li>
      <li
        className={classNames('li-cell-actions-container', {
          'li-cell-actions-container--expanded':
            expandedLineItem === lineItem.localId,
        })}
      >
        <LineItemExpanded
          lineItem={lineItem}
          editAllowedForConfirmed={editAllowedForConfirmed()}
          elementToFocus={elementToFocusOnExpand}
          onSelectFulfilmentType={onSelectFulfilmentType}
        />
      </li>
    </ul>
  )
}

export const SaleLineItem = React.memo(SaleLineItemComponent, isEqual)
